// Open Positions — listing page, styled against the internal design
// system's "Listing Page" template (filter bar → row cards → pagination),
// using this app's own tokens rather than the reference doc's own brand
// palette, to match the My Applications redesign's visual language.
import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ChevronLeft,
  ChevronRight,
  Loader2,
  RefreshCcw,
  Search,
} from 'lucide-react';
import { useJobs } from 'src/hooks/useJobsData';

const PAGE_SIZE_OPTIONS = [10, 20, 50];

const formatDate = (dateValue) => {
  if (!dateValue) return '-';
  const parsed = new Date(dateValue);
  if (Number.isNaN(parsed.getTime())) return dateValue;
  return parsed.toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

const getJobsListFromResponse = (response) => {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.data)) return response.data;
  if (Array.isArray(response?.data?.jobs)) return response.data.jobs;
  if (Array.isArray(response?.jobs)) return response.jobs;
  return [];
};

// ── Metadata row — plain text joined by small dot separators (§5.18) ───────
function MetaRow({ items }) {
  const parts = items.filter(Boolean);
  if (!parts.length) return null;
  return (
    <div
      className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs"
      style={{ color: 'var(--text-faint)' }}
    >
      {parts.map((part, i) => (
        // eslint-disable-next-line react/no-array-index-key
        <React.Fragment key={i}>
          {i > 0 && (
            <span
              className="h-[3px] w-[3px] flex-shrink-0 rounded-full"
              style={{ background: 'var(--border)' }}
              aria-hidden="true"
            />
          )}
          <span>{part}</span>
        </React.Fragment>
      ))}
    </div>
  );
}

// ── Row card (§5.18) — hover-elevates, left = info, right = divider + action.
function JobCard({ job }) {
  const jobId = job?.job_code;
  const locations = job?.locations || [];
  const locationLabel = locations.length
    ? `${locations.length} location${locations.length > 1 ? 's' : ''}`
    : 'Location not specified';
  const deadline = formatDate(
    job?.deadline || job?.requirement_info?.closing_date
  );

  return (
    <li
      className="group overflow-hidden rounded-[var(--radius-lg)] border transition-all duration-150"
      style={{
        background: 'var(--surface)',
        borderColor: 'var(--border-light)',
        boxShadow: 'var(--shadow-xs)',
      }}
    >
      <style>{`
        .op-row:hover { transform: translateY(-2px); }
      `}</style>
      <Link
        to={jobId ? `/openings/${jobId}` : '#'}
        aria-disabled={!jobId}
        className="op-row flex flex-col gap-3 p-4 transition-all duration-150 focus-visible:outline-none focus-visible:ring-2 sm:flex-row sm:items-center sm:justify-between sm:gap-4 sm:p-5"
        style={{ '--tw-ring-color': 'var(--ring)' }}
        onMouseEnter={(e) => {
          e.currentTarget.parentElement.style.borderColor = 'var(--primary)';
          e.currentTarget.parentElement.style.boxShadow = 'var(--shadow-md)';
          e.currentTarget.style.background = 'var(--primary-wash)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.parentElement.style.borderColor =
            'var(--border-light)';
          e.currentTarget.parentElement.style.boxShadow = 'var(--shadow-xs)';
          e.currentTarget.style.background = 'transparent';
        }}
      >
        <div className="min-w-0 flex-1">
          <p
            className="truncate text-sm font-semibold"
            style={{ color: 'var(--text)', fontFamily: 'var(--font-display)' }}
          >
            {job?.title || 'Untitled opening'}
          </p>
          <MetaRow
            items={[
              locationLabel,
              <span key="deadline">
                Deadline: <span className="tabular-nums">{deadline}</span>
              </span>,
              job?.job_code && (
                <span key="code">
                  Job Code: <span className="tabular-nums">{job.job_code}</span>
                </span>
              ),
            ]}
          />
        </div>
        <div className="flex flex-shrink-0 items-center justify-end gap-3">
          <span
            className="hidden h-8 w-px sm:block"
            style={{ background: 'var(--border-light)' }}
            aria-hidden="true"
          />
          <span
            className="inline-flex flex-shrink-0 items-center gap-1 rounded-[var(--radius-sm)] border px-3.5 py-1.5 text-xs font-semibold transition-colors group-hover:border-[var(--primary)] group-hover:text-[var(--primary)]"
            style={{ borderColor: 'var(--border)', color: 'var(--text-sec)' }}
          >
            View
            <ChevronRight className="h-3.5 w-3.5" aria-hidden="true" />
          </span>
        </div>
      </Link>
    </li>
  );
}

// ── Filter bar (§5.20) — search only (no categorical field in the list payload)
function FilterBar({ search, onSearchChange, hasActiveFilter, onClear }) {
  return (
    <div className="mb-4 flex flex-wrap items-center gap-2.5">
      {hasActiveFilter && (
        <button
          type="button"
          onClick={onClear}
          className="text-xs font-medium underline-offset-2 hover:underline"
          style={{ color: 'var(--text-faint)' }}
        >
          Clear
        </button>
      )}
      <div className="relative ml-auto w-full sm:w-[260px]">
        <Search
          className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2"
          style={{ color: 'var(--text-faint)' }}
          aria-hidden="true"
        />
        <input
          type="text"
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder="Search by role, location or job code"
          className="w-full rounded-[var(--radius-sm)] border py-2 pl-8 pr-3 text-xs focus:outline-none focus:ring-2"
          style={{
            borderColor: 'var(--border)',
            color: 'var(--text)',
            background: 'var(--surface)',
            '--tw-ring-color': 'var(--ring)',
          }}
        />
      </div>
    </div>
  );
}

// ── Pagination (§5.19) — rows-per-page, info text, numbered page buttons ───
function buildPageList(current, total) {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const keep = new Set([
    1,
    2,
    total - 1,
    total,
    current - 1,
    current,
    current + 1,
  ]);
  const sorted = [...keep]
    .filter((p) => p >= 1 && p <= total)
    .sort((a, b) => a - b);
  const out = [];
  let prev = 0;
  sorted.forEach((p) => {
    if (prev && p - prev > 1) out.push(`gap-${p}`);
    out.push(p);
    prev = p;
  });
  return out;
}

function Pagination({ page, pageSize, total, onPageSizeChange, onPageChange }) {
  if (total <= PAGE_SIZE_OPTIONS[0] && total <= pageSize) return null;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const start = (page - 1) * pageSize + 1;
  const end = Math.min(page * pageSize, total);
  const pageBtnBase =
    'flex h-[30px] w-[30px] flex-shrink-0 items-center justify-center rounded-[var(--radius-sm)] border text-xs font-semibold transition-colors';

  return (
    <div
      className="mt-5 flex flex-wrap items-center justify-between gap-3 border-t pt-4"
      style={{ borderColor: 'var(--border-light)' }}
    >
      <div className="flex items-center gap-2">
        <span className="text-xs" style={{ color: 'var(--text-faint)' }}>
          Rows per page
        </span>
        <select
          value={pageSize}
          onChange={(e) => onPageSizeChange(Number(e.target.value))}
          className="rounded-[var(--radius-xs)] border px-1.5 py-1 text-xs"
          style={{
            borderColor: 'var(--border)',
            color: 'var(--text)',
            background: 'var(--surface)',
          }}
        >
          {PAGE_SIZE_OPTIONS.map((n) => (
            <option key={n} value={n}>
              {n}
            </option>
          ))}
        </select>
      </div>

      <span className="text-xs" style={{ color: 'var(--text-faint)' }}>
        {start}–{end} of {total}
      </span>

      <div className="flex items-center gap-1">
        <button
          type="button"
          onClick={() => onPageChange(page - 1)}
          disabled={page <= 1}
          aria-label="Previous page"
          className={pageBtnBase}
          style={{
            borderColor: 'var(--border)',
            color: 'var(--text-sec)',
            background: 'var(--surface)',
            opacity: page <= 1 ? 0.4 : 1,
          }}
        >
          <ChevronLeft className="h-3.5 w-3.5" aria-hidden="true" />
        </button>
        {buildPageList(page, totalPages).map((p) =>
          typeof p === 'string' ? (
            <span
              key={p}
              className="px-0.5 text-xs"
              style={{ color: 'var(--text-faint)' }}
            >
              …
            </span>
          ) : (
            <button
              key={p}
              type="button"
              onClick={() => onPageChange(p)}
              className={pageBtnBase}
              style={
                p === page
                  ? {
                      background: 'var(--primary)',
                      color: 'var(--primary-fg)',
                      borderColor: 'var(--primary)',
                    }
                  : {
                      borderColor: 'var(--border)',
                      color: 'var(--text-sec)',
                      background: 'var(--surface)',
                    }
              }
            >
              {p}
            </button>
          )
        )}
        <button
          type="button"
          onClick={() => onPageChange(page + 1)}
          disabled={page >= totalPages}
          aria-label="Next page"
          className={pageBtnBase}
          style={{
            borderColor: 'var(--border)',
            color: 'var(--text-sec)',
            background: 'var(--surface)',
            opacity: page >= totalPages ? 0.4 : 1,
          }}
        >
          <ChevronRight className="h-3.5 w-3.5" aria-hidden="true" />
        </button>
      </div>
    </div>
  );
}

export default function Openings() {
  const { data: response, isLoading, isError, error, refetch } = useJobs();
  const allJobs = isLoading || isError ? [] : getJobsListFromResponse(response);

  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE_OPTIONS[0]);

  const searchLower = search.trim().toLowerCase();
  const filteredJobs = useMemo(
    () =>
      allJobs.filter((job) => {
        if (!searchLower) return true;
        const title = String(job?.title || '').toLowerCase();
        const code = String(job?.job_code || '').toLowerCase();
        const locationNames = (job?.locations || [])
          .map((l) => (typeof l === 'string' ? l : l?.name))
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        return (
          title.includes(searchLower) ||
          code.includes(searchLower) ||
          locationNames.includes(searchLower)
        );
      }),
    [allJobs, searchLower]
  );

  const pagedJobs = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredJobs.slice(start, start + pageSize);
  }, [filteredJobs, page, pageSize]);

  const clearFilters = () => {
    setSearch('');
    setPage(1);
  };

  return (
    <div className="mx-auto w-full max-w-[1100px] px-4 pb-12 pt-6 text-left sm:px-7 sm:pt-8">
      {/* Page header */}
      <div className="mb-5">
        <h1
          className="text-xl font-bold tracking-tight sm:text-2xl"
          style={{
            fontFamily: 'var(--font-display)',
            color: 'var(--text)',
            letterSpacing: '-0.02em',
          }}
        >
          Open Positions
        </h1>
        {!isLoading && !isError && (
          <p className="mt-1 text-sm" style={{ color: 'var(--text-muted)' }}>
            Roles currently open across the organization.{' '}
            <span style={{ color: 'var(--text-faint)' }}>
              {allJobs.length} open
            </span>
          </p>
        )}
      </div>

      {/* Loading */}
      {isLoading && (
        <div
          className="flex items-center gap-3 rounded-[var(--radius-md)] border p-6"
          style={{ background: 'var(--surface)', borderColor: 'var(--border)' }}
        >
          <Loader2
            className="h-4 w-4 animate-spin"
            style={{ color: 'var(--primary)' }}
            aria-hidden="true"
          />
          <span className="text-sm" style={{ color: 'var(--text-muted)' }}>
            Loading openings…
          </span>
        </div>
      )}

      {/* Error */}
      {isError && (
        <div
          className="rounded-[var(--radius-md)] border p-6"
          style={{
            background: 'var(--destructive-light)',
            borderColor: 'var(--destructive)',
          }}
        >
          <p className="text-sm" style={{ color: 'var(--destructive)' }}>
            {error?.message || 'Unable to fetch openings'}
          </p>
          <button
            type="button"
            onClick={() => refetch()}
            className="mt-4 inline-flex items-center gap-2 rounded-[var(--radius-sm)] border px-3 py-1.5 text-sm font-medium transition-colors"
            style={{
              borderColor: 'var(--destructive)',
              color: 'var(--destructive)',
              background: 'var(--surface)',
            }}
          >
            <RefreshCcw className="h-3.5 w-3.5" aria-hidden="true" />
            Retry
          </button>
        </div>
      )}

      {/* Empty state (no openings at all) */}
      {!isLoading && !isError && allJobs.length === 0 && (
        <div
          className="flex flex-col items-center gap-3 rounded-[var(--radius-lg)] border py-14 text-center"
          style={{
            background: 'var(--surface)',
            borderColor: 'var(--border-light)',
          }}
        >
          <p className="text-sm" style={{ color: 'var(--text-faint)' }}>
            No openings are available right now.
          </p>
        </div>
      )}

      {/* Filter bar + job list */}
      {!isLoading && !isError && allJobs.length > 0 && (
        <>
          <FilterBar
            search={search}
            onSearchChange={(v) => {
              setSearch(v);
              setPage(1);
            }}
            hasActiveFilter={!!search}
            onClear={clearFilters}
          />

          {pagedJobs.length > 0 ? (
            <ul className="flex flex-col gap-3">
              {pagedJobs.map((job) => (
                <JobCard key={job?.job_code || job?.id} job={job} />
              ))}
            </ul>
          ) : (
            <div
              className="rounded-[var(--radius-lg)] border py-10 text-center"
              style={{
                background: 'var(--surface)',
                borderColor: 'var(--border-light)',
              }}
            >
              <p className="text-sm" style={{ color: 'var(--text-faint)' }}>
                No results match your search.
              </p>
            </div>
          )}

          <Pagination
            page={page}
            pageSize={pageSize}
            total={filteredJobs.length}
            onPageSizeChange={(n) => {
              setPageSize(n);
              setPage(1);
            }}
            onPageChange={setPage}
          />
        </>
      )}
    </div>
  );
}
